import React, { useEffect, useState } from 'react'
import type { Session, User } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'
import { sendEvent, setLoggerUserId } from '@/lib/logger'
import type { AuthProviderProps } from './AuthTypes'
import { AuthContext } from './AuthContextDefinition'

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
      setUser(session?.user ?? null)
      setLoggerUserId(session?.user?.id)
      if (session?.user) {
        supabase.rpc('bm_link_current_user').then()
      }
      setLoading(false)
    })

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session)
      setUser(session?.user ?? null)
      setLoggerUserId(session?.user?.id)
      setLoading(false)

      if (event === 'SIGNED_IN' && session?.user) {
        // Link any band_members rows that match this user's email
        supabase.rpc('bm_link_current_user').then()

        sendEvent('auth.login', 'User logged in', 'INFO', {
          metadata: {
            provider: session.user.app_metadata?.provider,
            userId: session.user.id,
          },
        })
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })
    return { error }
  }

  const signInWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin,
      },
    })
    return { error }
  }

  const signOut = async () => {
    const { error } = await supabase.auth.signOut()
    return { error }
  }

  const value = {
    user,
    session,
    loading,
    signIn,
    signInWithGoogle,
    signOut,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
